import React from 'react';
import {
  Shield,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  TrendingUp,
  TrendingDown,
  Users,
  Building,
  Eye,
  MoreHorizontal,
  Calendar,
  Lock,
  UserCheck,
  Zap
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import StatCard from '../../components/StatCard';

const ComplianceDashboard: React.FC = () => {
  const { user } = useAuth();


  const complianceMetrics = [
    { month: 'Jul', s8Compliance: 98, auditScore: 95, incidents: 2 },
    { month: 'Aug', s8Compliance: 97, auditScore: 96, incidents: 1 },
    { month: 'Sep', s8Compliance: 99, auditScore: 98, incidents: 0 },
    { month: 'Oct', s8Compliance: 98, auditScore: 97, incidents: 1 },
    { month: 'Nov', s8Compliance: 100, auditScore: 99, incidents: 0 },
    { month: 'Dec', s8Compliance: 99, auditScore: 98, incidents: 1 }
  ];

  const s8Activities = [
    { id: 'S8-001', medication: 'OxyContin 20mg', action: 'Dispensed', pharmacist: 'Dr. Sarah Johnson', time: '10:30 AM', patient: 'John Smith' },
    { id: 'S8-002', medication: 'Morphine 10mg', action: 'Stock Count', pharmacist: 'Dr. Michael Chen', time: '09:15 AM', patient: 'N/A' },
    { id: 'S8-003', medication: 'Fentanyl Patch', action: 'Dispensed', pharmacist: 'Dr. Sarah Johnson', time: '08:45 AM', patient: 'Mary Wilson' },
    { id: 'S8-004', medication: 'Tramadol 50mg', action: 'Received', pharmacist: 'Dr. Michael Chen', time: '08:00 AM', patient: 'N/A' }
  ];

  const complianceAlerts = [
    {
      id: 'comp-001',
      type: 's8_witness',
      severity: 'high',
      title: 'S8 Witness Required',
      message: 'OxyContin dispensing requires pharmacist witness',
      time: '5 min ago',
      status: 'pending'
    },
    {
      id: 'comp-002',
      type: 'audit_due',
      severity: 'medium',
      title: 'Monthly S8 Audit Due',
      message: 'December S8 stock reconciliation due in 3 days',
      time: '2 hours ago',
      status: 'pending'
    },
    {
      id: 'comp-003',
      type: 'access_log',
      severity: 'low',
      title: 'S8 Cabinet Access',
      message: 'Unusual access pattern detected - 5 accesses in 1 hour',
      time: '4 hours ago',
      status: 'reviewed'
    }
  ];

  const regulatoryStatus = [
    { name: 'PBS Compliance', value: 98, color: '#10B981' },
    { name: 'S8 Regulations', value: 99, color: '#8B5CF6' },
    { name: 'TGA Guidelines', value: 96, color: '#F59E0B' },
    { name: 'State Regulations', value: 97, color: '#EF4444' }
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Compliance Dashboard 🛡️
          </h1>
          <p className="text-gray-600 mt-2">Regulatory compliance monitoring and S8 controlled substances management for {user?.branch}.</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Last audit</p>
          <p className="text-sm font-medium text-gray-900">{format(new Date(), 'dd MMM yyyy')}</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="S8 Compliance Score"
          value="99%"
          icon={<Shield className="h-6 w-6" />}
          trend="vs last month"
          trendValue={1}
          color="text-green-600"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Audit Score"
          value="98%"
          icon={<CheckCircle className="h-6 w-6" />}
          trend="vs last audit"
          trendValue={2}
          color="text-blue-600"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Active S8 Items"
          value="24"
          icon={<Lock className="h-6 w-6" />}
          trend="vs last month"
          trendValue={0}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          title="Compliance Incidents"
          value="1"
          icon={<AlertTriangle className="h-6 w-6" />}
          trend="this month"
          trendValue={-50}
          color="text-orange-600"
          bgGradient="bg-gradient-to-br from-orange-500 to-orange-600"
        />
      </div>

      {/* Compliance Alerts */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-orange-600" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">Compliance Alerts</h3>
              <p className="text-gray-600 text-sm">Active compliance issues requiring attention</p>
            </div>
          </div>
          <Link to="/alerts" className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center space-x-1">
            <span>View All</span>
            <Eye className="h-4 w-4" />
          </Link>
        </div>
        
        <div className="space-y-4">
          {complianceAlerts.map((alert) => (
            <div key={alert.id} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl hover:bg-gray-100/50 transition-colors">
              <div className="flex items-center space-x-4">
                <div className={`p-2 rounded-lg ${
                  alert.severity === 'high' ? 'bg-red-100 text-red-600' :
                  alert.severity === 'medium' ? 'bg-orange-100 text-orange-600' :
                  'bg-yellow-100 text-yellow-600'
                }`}>
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{alert.title}</h4>
                  <p className="text-sm text-gray-600">{alert.message}</p>
                  <p className="text-xs text-gray-400 mt-1">{alert.time}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  alert.status === 'pending' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                }`}>
                  {alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
                </span>
                <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                  Review
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Compliance Trends */}
        <div className="lg:col-span-2">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Compliance Trends</h3>
                <p className="text-gray-600 text-sm">Monthly compliance scores and incident tracking</p>
              </div>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <MoreHorizontal className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={complianceMetrics}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: 'none', 
                    borderRadius: '12px', 
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                  }} 
                />
                <Line type="monotone" dataKey="s8Compliance" stroke="#8B5CF6" strokeWidth={3} />
                <Line type="monotone" dataKey="auditScore" stroke="#10B981" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Regulatory Status */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Regulatory Status</h3>
              <p className="text-gray-600 text-sm">Compliance across different regulations</p>
            </div>
          </div>
          <div className="space-y-4">
            {regulatoryStatus.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm font-medium text-gray-900">{item.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full" 
                      style={{ width: `${item.value}%`, backgroundColor: item.color }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{item.value}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent S8 Activities */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Recent S8 Activities</h3>
            <p className="text-gray-600 text-sm">Latest controlled substance transactions and activities</p>
          </div>
          <Link to="/compliance/s8-register" className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center space-x-1">
            <span>View S8 Register</span>
            <Eye className="h-4 w-4" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Medication</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pharmacist</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {s8Activities.map((activity) => (
                <tr key={activity.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{activity.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{activity.medication}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      activity.action === 'Dispensed' ? 'bg-green-100 text-green-800' :
                      activity.action === 'Received' ? 'bg-blue-100 text-blue-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {activity.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{activity.pharmacist}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{activity.time}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{activity.patient}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ComplianceDashboard;
